/**
 * SFDA-858: sign-in through Zitadel, behind
 * REACT_APP_AUTH_PROVIDER=zitadel. Auth0 stays the default.
 *
 * This module stays free of oidc-client-ts so Auth0 builds don't ship it;
 * ZitadelAuthProvider (which needs it) is loaded lazily only in zitadel mode.
 * (The small /welcome and /callback pages for zitadel mode are in every build.)
 */
import { createContext, useContext } from 'react';
import { env } from '../../env';
import { isE2eAuthEnabled } from '../../utils/e2eAuth';
import { isSessionUsable, readSession } from './tokens';

/**
 * The jst e2e suite (staging builds only, REACT_APP_ENABLE_E2E_AUTH) signs in
 * with an Auth0 machine token in `?e2e_token=`, which AuthProvider turns into a
 * session. Until it has a Zitadel equivalent, such a page (or one still holding
 * that session) keeps using AuthProvider; the API accepts Auth0 tokens in
 * zitadel mode too. Decided once, before AuthProvider strips the parameter.
 */
function hasE2eSession(): boolean {
  if (!isE2eAuthEnabled()) return false;
  try {
    if (new URLSearchParams(window.location.search).has('e2e_token')) return true;
    const session = readSession();
    return session?.source === 'e2e' && isSessionUsable(session);
  } catch {
    // Storage blocked: no stored session either.
    return false;
  }
}

export const isZitadelAuth =
  env.AUTH_PROVIDER === 'zitadel' && !hasE2eSession();

/** The signed-in person has no JST account (or lost it). */
export class NoAccountError extends Error {
  constructor() {
    super('no_account');
    this.name = 'NoAccountError';
  }
}

export interface ZitadelActions {
  /** Leaves for Zitadel (our login page); comes back to /callback, then `returnTo`. */
  signIn: (returnTo: string) => Promise<void>;
  /** Finishes the sign-in on /callback and returns where to go next. */
  completeSignIn: () => Promise<string>;
}

export const ZitadelContext = createContext<ZitadelActions | null>(null);

export function useZitadel(): ZitadelActions {
  const context = useContext(ZitadelContext);
  if (!context) {
    throw new Error('useZitadel must be used within ZitadelAuthProvider');
  }
  return context;
}
